/**
 * Cài đặt › Module (ADR-025 §8): integration modules by business branch
 * (subsystems/platform/facade/adapters/admin-api/src/admin/modules.rs). Every form is drawn from the module's manifest and
 * settings schema; secrets are write-only (KEEP / REFERENCE / VALUE), never returned.
 */

import type { CallOptions, ISODateTime, WriteOptions } from '../types';
import type { ApprovalLink, IntegrationEnvironment, SecretInput, SecretStatus, StaffRef } from './integrations';

/** A business branch (closed set, ADR-025 §1, ADR-028; connector-kit `BranchId`). */
export type ModuleBranchId =
  | 'registry'
  | 'payment'
  | 'notify'
  | 'einvoice'
  | 'captcha'
  | 'signing'
  | 'ekyc'
  | 'ocr'
  | 'regulatory'
  | 'lookup'
  | 'escrow'
  | 'dns'
  | 'hosting'
  | 'email'
  | 'cloud'
  | 'website'
  | 'addons';

/** The branches in the order of the tabs (`BranchId::ALL`). */
export const MODULE_BRANCHES: readonly ModuleBranchId[] = [
  'registry',
  'payment',
  'notify',
  'einvoice',
  'captcha',
  'signing',
  'ekyc',
  'ocr',
  'regulatory',
  'lookup',
  'escrow',
  'dns',
  'hosting',
  'email',
  'cloud',
  'website',
  'addons',
];

/** A label the server sends in both languages. */
export interface BilingualText {
  vi: string;
  en: string;
}

export interface ModuleChoice {
  value: string;
  label: BilingualText;
}

export const MODULE_UNITS = ['ms', 'seconds', 'minutes', 'vnd', 'records', 'attempts', 'percent'] as const;
export type ModuleUnit = (typeof MODULE_UNITS)[number];

/** What a field holds, and therefore which control the form draws. */
export type ModuleFieldKind =
  | { type: 'text'; max_len: number; pattern: string | null }
  | { type: 'secret'; max_len: number }
  | { type: 'select'; options: ModuleChoice[]; options_from: string | null }
  | { type: 'multi_select'; options: ModuleChoice[] }
  | { type: 'url'; https_only: boolean }
  | { type: 'path' }
  | { type: 'integer'; min: number; max: number; unit: ModuleUnit | null }
  | { type: 'boolean' }
  | { type: 'time_of_day' }
  | { type: 'email' }
  | { type: 'phone' }
  | { type: 'certificate'; private_key: boolean }
  | { type: 'file'; mime: string[]; max_bytes: number };
export type ModuleFieldType = ModuleFieldKind['type'];
export const MODULE_FIELD_TYPES: readonly ModuleFieldType[] = [
  'text',
  'secret',
  'select',
  'multi_select',
  'url',
  'path',
  'integer',
  'boolean',
  'time_of_day',
  'email',
  'phone',
  'certificate',
  'file',
];

export type ModuleRequired = { when: 'always' } | { when: 'when_enabled' } | { when: 'never' } | { when: 'if'; field: string; equals: string };

export interface ModuleField {
  key: string;
  label: BilingualText;
  help: BilingualText | null;
  kind: ModuleFieldKind;
  required: ModuleRequired;
  default: string | number | boolean | string[] | null;
  advanced: boolean;
  group: string | null;
  /** Read only in `placement_settings` (ADR-032 §2.5): a plan may override the server group's value. */
  overridable: boolean;
}

/** How a module's own option type is drawn (connector-kit `OptionRender`, ADR-032 §2.7). */
export type ModuleOptionRender = { kind: 'select' } | { kind: 'radio' } | { kind: 'quantity'; min: number; max: number; unit: ModuleUnit | null };
/** Where its values come from (`OptionValues`): typed by staff, a list action of the module, or the server groups of the same module. */
export type ModuleOptionValues = { kind: 'typed' } | { kind: 'from_server'; action: string } | { kind: 'placement_groups' };
/** What a chosen value does (`OptionEffect`): overrides a placement setting, or picks the service's server group. */
export type ModuleOptionEffect = { kind: 'setting'; key: string } | { kind: 'placement_group' };

/** An option type a module adds to "Chọn kiểu trường" of the products whose plans use it (`OptionTypeSpec`). */
export interface ModuleOptionType {
  /** Unique in the module; stored as `<module>:<key>` in `option_groups.module_type`. */
  key: string;
  label: BilingualText;
  help: BilingualText | null;
  render: ModuleOptionRender;
  values: ModuleOptionValues;
  effect: ModuleOptionEffect;
}

export const MODULE_TEST_STEPS = ['REACHABILITY', 'TOKEN', 'API_CALL', 'SIGNATURE', 'SEND_TO_ADMIN'] as const;
export type ModuleTestStepCode = (typeof MODULE_TEST_STEPS)[number];

export type ModuleDataClass = 'public' | 'personal' | 'regulated';
export type ModuleDisablePolicy = 'immediate' | 'needs_approval';

export interface ModuleManifest {
  id: string;
  branch: ModuleBranchId;
  name: BilingualText;
  summary: BilingualText;
  vendor: { name: string; website: string };
  version: string;
  docs: { kind: 'filed' | 'public'; location: string }[];
  capabilities: string[];
  settings: { fields: ModuleField[] };
  /** Placement parameters (ADR-032 §2.5): every field on a server group, the overridable ones on a plan. */
  placement_settings: { fields: ModuleField[] };
  /** The read-only admin action listing the provider's plans for `provider_plan`; null: typed freely (ADR-032 §2.4). */
  plan_source: string | null;
  option_types: ModuleOptionType[];
  instances: { kind: 'one' } | { kind: 'many'; max: number };
  test: { kind: 'steps'; steps: ModuleTestStepCode[] } | { kind: 'no_test'; reason: BilingualText };
  /** `get`: the provider calls GET with its data in the query string (VNPAY); POST is always accepted. */
  webhook: { max_body: number; answer_within_seconds: number; get: boolean } | null;
  jobs: { key: string; label: BilingualText; schedule: { kind: 'every'; seconds: number } | { kind: 'daily_at'; setting: string; default: string } }[];
  events: { key: string; label: BilingualText }[];
  admin_actions: { key: string; label: BilingualText; permission: string; mutating: boolean; input: { fields: ModuleField[] } | null }[];
  pages: { key: string; label: BilingualText; target: { kind: 'route' | 'component'; value: string } }[];
  egress:
    | { kind: 'hosts'; hosts: string[] }
    | { kind: 'from_setting'; setting: string }
    | { kind: 'local_only' }
    | { kind: 'none' };
  data: ModuleDataClass;
  disable: ModuleDisablePolicy;
  production_ready: boolean;
}

/** NEW: nothing saved yet; DRAFT / TESTED: a draft (TESTED: its content passed a test within 24 h);
 * PENDING: waiting for a second person; ACTIVE / DISABLED: the version in force is on / off. */
export const MODULE_INSTANCE_STATUSES = ['NEW', 'DRAFT', 'TESTED', 'PENDING', 'ACTIVE', 'DISABLED'] as const;
export type ModuleInstanceStatus = (typeof MODULE_INSTANCE_STATUSES)[number];
export const MODULE_HEALTH = ['healthy', 'degraded', 'down', 'unknown'] as const;
export type ModuleHealth = (typeof MODULE_HEALTH)[number];
export const MODULE_VERSION_STATUSES = ['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'SUPERSEDED', 'REJECTED', 'CANCELLED'] as const;
export type ModuleVersionStatus = (typeof MODULE_VERSION_STATUSES)[number];

export interface ModuleVersion {
  id: string;
  version: number;
  status: ModuleVersionStatus;
  enabled: boolean;
  values: Record<string, unknown>;
  secrets: Record<string, SecretStatus>;
  content_sha256: string;
  /** A test of this exact content passed within 24 hours (always true for a module without a test). */
  tested: boolean;
  reason: string;
  created_by: StaffRef | null;
  created_at: ISODateTime;
  activated_by: StaffRef | null;
  activated_at: ISODateTime | null;
  approval: ApprovalLink | null;
}

export const MODULE_STEP_OUTCOMES = ['OK', 'FAILED', 'SKIPPED'] as const;
export type ModuleStepOutcome = (typeof MODULE_STEP_OUTCOMES)[number];

export interface ModuleTestStep {
  step: ModuleTestStepCode;
  outcome: ModuleStepOutcome;
  /** A failure code (TIMEOUT, UNAUTHORIZED, …); shown with its meaning when one is known. */
  code: string | null;
  detail: string | null;
  duration_ms: number;
}

export interface ModuleTest {
  id: string;
  outcome: 'OK' | 'FAILED';
  steps: ModuleTestStep[];
  version_id: string | null;
  content_sha256: string | null;
  started_at: ISODateTime;
  finished_at: ISODateTime;
}

/** A service instance that loaded the instance's configuration. */
export interface ModuleAppliedBy {
  service: string;
  instance: string;
  /** `settings.applied_configs.subject`: the instance's, or the provider's former one. */
  subject?: string;
  /** Reported under the provider's former name (`provider:…`), from before the switch to modules. */
  former_subject?: boolean;
  source: 'DATABASE' | 'ENVIRONMENT' | 'DISABLED';
  generation: number | null;
  applied_at: ISODateTime;
  checked_at: ISODateTime;
  last_error: { code: string; message: string } | null;
  up_to_date: boolean;
}

export interface ModuleInstance {
  id: string;
  key: string;
  display_name: string | null;
  status: ModuleInstanceStatus;
  enabled: boolean;
  generation: number | null;
  active: ModuleVersion | null;
  draft: ModuleVersion | null;
  pending: ModuleVersion | null;
  recent_tests: ModuleTest[];
  applied_by: ModuleAppliedBy[];
  health: ModuleHealth;
  /** `/hooks/{branch}/{module}/{instance}` when the module receives webhooks. */
  callback_path: string | null;
}

export interface ModuleSummary {
  manifest: ModuleManifest;
  /** The branch's fields every module of it carries in front of its own. */
  common_settings: ModuleField[];
  instances: ModuleInstance[];
  can_add: boolean;
}

/** A provider of ADR-016 not wrapped as a module yet: configured on its own page. */
export interface LegacyModule {
  provider: string;
  module: string;
  name: BilingualText;
  summary: BilingualText;
  vendor: { name: string; website: string };
  page: string;
  configured_in_database: boolean;
  enabled: boolean | null;
  version: number | null;
}

/** The registry branch's connections (their own pages, ADR-016). */
export interface ModuleRegistryConnection {
  id: string;
  code: string;
  name: string;
  connection_kind: string;
  protocol: string;
  module: string;
  status: string;
  page: string;
}

export interface ModuleBranch {
  id: ModuleBranchId;
  number: number;
  name: BilingualText;
  core_settings: ModuleField[];
  /** The branch core's own settings as the instance `core` / `default` (no connection test, never
   * switched off); null when the core declares none. */
  core: ModuleSummary | null;
  modules: ModuleSummary[];
  legacy: LegacyModule[];
  connections: ModuleRegistryConnection[] | null;
}

interface Caller {
  environment: IntegrationEnvironment;
  can_manage: boolean;
  can_manage_secrets: boolean;
}

export interface ModulesOverview extends Caller {
  branches: ModuleBranch[];
}

export interface ModuleBranchDetail extends Caller {
  branch: ModuleBranch;
}

export interface ModuleInstanceDetail extends Caller {
  instance: ModuleInstance;
  manifest: ModuleManifest;
  common_settings: ModuleField[];
}

export interface ModuleVersionPage {
  items: ModuleVersion[];
  next_before_version: number | null;
}

export const MODULE_CALL_OUTCOMES = ['ok', 'refused', 'not_sent', 'unknown'] as const;
export type ModuleCallOutcome = (typeof MODULE_CALL_OUTCOMES)[number];

export interface ModuleCall {
  id: string;
  operation: string;
  outcome: ModuleCallOutcome;
  /** The refusal code or the failure class. */
  code: string | null;
  provider_code: string | null;
  duration_ms: number;
  module_version: string;
  correlation_id: string | null;
  started_at: ISODateTime;
}

export const MODULE_RECEIPT_OUTCOMES = ['PROCESSED', 'DUPLICATE', 'MALFORMED', 'FAILED'] as const;
export type ModuleReceiptOutcome = (typeof MODULE_RECEIPT_OUTCOMES)[number];

/** One authenticated webhook call: never its body. */
export interface ModuleWebhookReceipt {
  id: string;
  received_at: ISODateTime;
  body_sha256: string;
  body_size: number;
  outcome: ModuleReceiptOutcome;
  external_ids: string[];
  http_status: number;
  duration_ms: number;
  correlation_id: string | null;
}

export interface ModuleLogPage<T> {
  items: T[];
  next_before: ISODateTime | null;
}

export interface CreateModuleInstanceBody {
  key: string;
  display_name?: string | null;
  reason: string;
}

export interface SaveModuleDraftBody {
  enabled: boolean;
  values: Record<string, unknown>;
  secrets: Record<string, SecretInput>;
  base_version_id?: string | null;
  display_name?: string | null;
  reason: string;
}

export interface ModuleActionBody {
  input: Record<string, unknown>;
  reason?: string;
}

/** A note on a saved draft: the host's (PRIVATE_ADDRESS, PLAIN_HTTP) or the module's own code. */
export interface ModuleWarning {
  code: string;
  field: string | null;
}

export interface ModuleCreateResult {
  result: 'CREATED';
  item: ModuleInstance;
}

export interface ModuleDraftResult {
  result: 'DRAFT_SAVED';
  item: ModuleVersion;
  warnings: ModuleWarning[];
}

export interface ModuleChangeResult {
  /** TEST_REQUIRED (switching on): the draft was saved; test it, then propose it. */
  result: 'APPLIED' | 'PENDING_APPROVAL' | 'TEST_REQUIRED';
  item: ModuleVersion;
  approval: ApprovalLink | null;
  warnings: ModuleWarning[];
}

export interface ModuleTestResult {
  test: ModuleTest;
  version: number;
}

export interface ModuleActionResult {
  outcome: ModuleCallOutcome;
  value?: unknown;
  refusal?: { code: string; provider_code: string | null; message: string | null; http_status: number | null };
  failure?: { class: string; detail: string };
}

/** The module id of a branch core's own settings (`platform_connector_sdk::CORE_MODULE`). */
export const CORE_MODULE = 'core';

export interface ModulesApi {
  getModules(opts?: CallOptions): Promise<ModulesOverview>;
  getModuleBranch(branch: ModuleBranchId, opts?: CallOptions): Promise<ModuleBranchDetail>;
  getModuleInstance(branch: ModuleBranchId, module: string, key: string, opts?: CallOptions): Promise<ModuleInstanceDetail>;
  listModuleVersions(
    branch: ModuleBranchId,
    module: string,
    key: string,
    query: { before_version?: number; limit?: number },
    opts?: CallOptions,
  ): Promise<ModuleVersionPage>;
  listModuleCalls(branch: ModuleBranchId, module: string, key: string, query: { before?: ISODateTime; limit?: number }, opts?: CallOptions): Promise<ModuleLogPage<ModuleCall>>;
  listModuleWebhooks(
    branch: ModuleBranchId,
    module: string,
    key: string,
    query: { before?: ISODateTime; limit?: number },
    opts?: CallOptions,
  ): Promise<ModuleLogPage<ModuleWebhookReceipt>>;
  createModuleInstance(branch: ModuleBranchId, module: string, body: CreateModuleInstanceBody, opts: WriteOptions): Promise<ModuleCreateResult>;
  saveModuleDraft(branch: ModuleBranchId, module: string, key: string, body: SaveModuleDraftBody, opts: WriteOptions): Promise<ModuleDraftResult>;
  proposeModuleVersion(branch: ModuleBranchId, module: string, key: string, versionId: string, body: { reason: string }, opts: WriteOptions): Promise<ModuleChangeResult>;
  testModuleInstance(branch: ModuleBranchId, module: string, key: string, body: { version_id?: string | null }, opts: WriteOptions): Promise<ModuleTestResult>;
  disableModuleInstance(branch: ModuleBranchId, module: string, key: string, body: { reason: string }, opts: WriteOptions): Promise<ModuleChangeResult>;
  enableModuleInstance(branch: ModuleBranchId, module: string, key: string, body: { reason: string }, opts: WriteOptions): Promise<ModuleChangeResult>;
  runModuleAction(branch: ModuleBranchId, module: string, key: string, action: string, body: ModuleActionBody, opts?: CallOptions): Promise<ModuleActionResult>;
}
